import { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

export default function Pricing() {
  const [pricing, setPricing] = useState({ S: 0.5, M: 0.7, L: 1.0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/pricing-settings');
      setPricing(r.data);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load pricing' });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const vals = { S: parseFloat(pricing.S), M: parseFloat(pricing.M), L: parseFloat(pricing.L) };
    if (Object.values(vals).some(v => isNaN(v) || v < 0.01 || v > 100)) {
      setMessage({ type: 'error', text: 'Each price must be a number between 0.01 and 100 GEL' });
      return;
    }
    if (!window.confirm(`Set prices to S ${vals.S.toFixed(2)} / M ${vals.M.toFixed(2)} / L ${vals.L.toFixed(2)} GEL per hour? ` +
                        'This applies immediately to every parcel, including ones already in lockers.')) return;
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.put('/admin/pricing-settings', vals);
      setMessage({ type: 'success', text: r.data.message || 'Pricing updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to save' });
    }
    setSaving(false);
  };

  // Keep what was typed (even an empty field while editing); checked on save.
  const handleChange = (size, value) => {
    setPricing(prev => ({ ...prev, [size]: value }));
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Pricing Settings</h1>
          <p style={s.sub}>Manage hourly rates for each box size. Changes apply immediately to all customers.</p>
        </div>
        <button style={s.refreshBtn} onClick={load}>
          <RefreshCw size={16} /> Reload
        </button>
      </div>

      {message && (
        <div style={{ ...s.banner, ...(message.type === 'error' ? s.error : s.success) }}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {message.text}
        </div>
      )}

      <div style={s.grid}>
        {[
          { size: 'S', label: 'Small', desc: '9 × 35 × 48 cm', color: '#4d90fe' },
          { size: 'M', label: 'Medium', desc: '20 × 35 × 48 cm', color: '#a78bfa' },
          { size: 'L', label: 'Large', desc: '28 × 35 × 48 cm', color: '#8BC63E' },
        ].map(box => (
          <div key={box.size} style={s.card}>
            <div style={{ ...s.sizeBadge, background: box.color + '22', color: box.color }}>
              {box.label}
            </div>
            <div style={s.sizeName}>{box.size}</div>
            <div style={s.sizeDesc}>{box.desc}</div>
            <div style={s.priceRow}>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                style={s.priceInput}
                value={pricing[box.size]}
                onChange={e => handleChange(box.size, e.target.value)}
              />
              <span style={s.priceUnit}>₾ / hour</span>
            </div>
          </div>
        ))}
      </div>

      <div style={s.actions}>
        <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div style={s.note}>
        💡 Pricing changes are applied immediately. The order summary on locker.damitove.ge, pickup payments and reminder SMS all use the new rates.
      </div>
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 20 },
  title: { fontSize: 28, fontWeight: 800, color: '#e4e5ed', marginBottom: 4 },
  sub: { fontSize: 14, color: '#8789a0', maxWidth: 560 },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'transparent', border: '1px solid #252733', color: '#8789a0', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  loading: { padding: 40, textAlign: 'center', color: '#8789a0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  card: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 24 },
  sizeBadge: { display: 'inline-block', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, marginBottom: 12 },
  sizeName: { fontSize: 36, fontWeight: 800, color: '#e4e5ed', marginBottom: 4 },
  sizeDesc: { fontSize: 13, color: '#8789a0', marginBottom: 20 },
  priceRow: { display: 'flex', alignItems: 'center', gap: 8 },
  priceInput: { width: 100, padding: '12px 14px', background: '#1a1b25', border: '1px solid #252733', borderRadius: 8, color: '#e4e5ed', fontSize: 18, fontWeight: 700 },
  priceUnit: { fontSize: 14, color: '#8789a0' },
  actions: { display: 'flex', justifyContent: 'flex-end', marginBottom: 16 },
  saveBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#8BC63E', border: 'none', color: '#07080c', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  banner: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 16 },
  success: { background: 'rgba(139,198,62,0.1)', color: '#8BC63E', border: '1px solid rgba(139,198,62,0.3)' },
  error: { background: 'rgba(255,68,102,0.1)', color: '#ff4466', border: '1px solid rgba(255,68,102,0.3)' },
  note: { padding: 16, background: '#1a1b25', borderRadius: 10, fontSize: 13, color: '#8789a0', lineHeight: 1.6 },
};
